import React, { memo, useMemo, useState, useCallback, useEffect } from 'react';
import moment, { Moment } from 'moment';
import { getDays, cloneMomentByYearAndMonth } from './utils';
import Year from './Year';
import Month from './Month';
import Calendar from './Calendar';
import Clear from './Clear';
import { useStyles } from './styles';

export interface PickerProps {
  date: string;
  min: string;
  max: string;
  onChange: (date?: string) => void;
}

function Picker({ date, min, max, onChange }: PickerProps) {
  const display = date || min || max;
  const activeMoment = useMemo(() => (display ? moment(display) : moment()), [
    display
  ]);
  const activeYear = activeMoment.year();
  const activeMonth = activeMoment.month();
  const activeDay = activeMoment.date();
  const minMoment = useMemo(() => (min ? moment(min) : undefined), [min]);
  const maxMoment = useMemo(() => (max ? moment(max) : undefined), [max]);
  const [year, setYear] = useState(activeYear);
  const [month, setMonth] = useState(activeMonth);
  const displayMoment = useMemo(
    () =>
      activeMoment
        .clone()
        .year(year)
        .month(month),
    [activeMoment, year, month]
  );
  const days = useMemo(() => getDays(displayMoment), [displayMoment]);
  const change = useCallback(
    (newMoment: Moment) => {
      if (onChange) {
        onChange(newMoment.format('YYYY-MM-DD'));
        setYear(newMoment.year());
        setMonth(newMoment.month());
      }
    },
    [onChange]
  );
  const clear = useCallback(() => onChange(), [onChange]);
  const getNewMoment = (
    day: number,
    prevMonth: boolean,
    nextMonth: boolean
  ): Moment => {
    const currentM = cloneMomentByYearAndMonth(displayMoment, year, month);

    if (prevMonth) {
      currentM.subtract(1, 'month');
    } else if (nextMonth) {
      currentM.add(1, 'month');
    }
    return currentM.date(day);
  };
  const styles = useStyles();

  useEffect(() => {
    if (activeYear !== year) {
      setYear(activeYear);
    }
    if (activeMonth !== month) {
      setMonth(activeMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [`${activeYear}-${activeMonth}`]);

  return (
    <div className={styles.datePicker}>
      <header className={styles.header}>
        <Year
          className={styles.selector}
          year={year}
          moment={displayMoment}
          minMoment={minMoment}
          maxMoment={maxMoment}
          onChange={setYear}
        />
        <Month
          className={styles.selector}
          month={month}
          moment={displayMoment}
          minMoment={minMoment}
          maxMoment={maxMoment}
          onChange={setMonth}
        />
      </header>
      <Calendar
        days={days}
        activeYear={activeYear}
        activeMonth={activeMonth}
        activeDay={activeDay}
        minMoment={minMoment}
        maxMoment={maxMoment}
        getNewMoment={getNewMoment}
        onChange={change}
      />
      <Clear onClear={clear} />
    </div>
  );
}

export default memo(Picker);
